import { useState } from 'react';

export interface QuizQuestion {
  id: string;
  question_text: string;
  options: string[];
  correct_index: number;
  order_index: number;
  option_explanations: string[];
}

interface QuizBuilderProps {
  questions: QuizQuestion[];
  onChange: (questions: QuizQuestion[]) => void;
}

function emptyQuestion(orderIndex: number): QuizQuestion {
  return {
    id: `q_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    question_text: '',
    options: ['', '', '', ''],
    correct_index: 0,
    order_index: orderIndex,
    option_explanations: ['', '', '', ''],
  };
}

export default function QuizBuilder({ questions, onChange }: QuizBuilderProps) {
  const [expandedId, setExpandedId] = useState<string | null>(
    questions.length > 0 ? questions[0].id : null
  );
  const [explanationOpenId, setExplanationOpenId] = useState<string | null>(null);

  const addQuestion = () => {
    const newQ = emptyQuestion(questions.length);
    const updated = [...questions, newQ];
    onChange(updated);
    setExpandedId(newQ.id);
  };

  const removeQuestion = (id: string) => {
    const updated = questions.filter((q) => q.id !== id).map((q, i) => ({ ...q, order_index: i }));
    onChange(updated);
    if (expandedId === id) setExpandedId(updated.length > 0 ? updated[0].id : null);
  };

  const updateQuestion = (id: string, patch: Partial<QuizQuestion>) => {
    onChange(questions.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  };

  const updateOption = (qId: string, optIdx: number, value: string) => {
    const q = questions.find((x) => x.id === qId);
    if (!q) return;
    const newOptions = [...q.options];
    newOptions[optIdx] = value;
    updateQuestion(qId, { options: newOptions });
  };

  const updateExplanation = (qId: string, optIdx: number, value: string) => {
    const q = questions.find((x) => x.id === qId);
    if (!q) return;
    const newExplanations = [...(q.option_explanations || [])];
    newExplanations[optIdx] = value;
    updateQuestion(qId, { option_explanations: newExplanations });
  };

  const addOption = (qId: string) => {
    const q = questions.find((x) => x.id === qId);
    if (!q || q.options.length >= 6) return;
    updateQuestion(qId, {
      options: [...q.options, ''],
      option_explanations: [...(q.option_explanations || []), ''],
    });
  };

  const removeOption = (qId: string, optIdx: number) => {
    const q = questions.find((x) => x.id === qId);
    if (!q || q.options.length <= 2) return;
    const newOptions = q.options.filter((_, i) => i !== optIdx);
    const newExplanations = (q.option_explanations || []).filter((_, i) => i !== optIdx);
    const newCorrect = q.correct_index >= newOptions.length ? newOptions.length - 1 : q.correct_index;
    updateQuestion(qId, { options: newOptions, correct_index: newCorrect, option_explanations: newExplanations });
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[#8B9D83] text-[10px] uppercase tracking-[3px] mb-1">Cuestionario</p>
          <p className="text-[#F8F6F3]/40 text-sm">{questions.length} pregunta{questions.length !== 1 ? 's' : ''} · Marca la correcta y añade explicaciones</p>
        </div>
        <button
          onClick={addQuestion}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
          style={{ background: 'rgba(139,157,131,0.15)', color: '#8B9D83', border: '1px solid rgba(139,157,131,0.25)' }}
        >
          <i className="ri-add-line" />
          Añadir pregunta
        </button>
      </div>

      {questions.length === 0 && (
        <div
          className="flex flex-col items-center justify-center py-14 rounded-2xl gap-3 cursor-pointer"
          style={{ background: 'rgba(248,246,243,0.02)', border: '1px dashed rgba(248,246,243,0.10)' }}
          onClick={addQuestion}
        >
          <div className="w-12 h-12 flex items-center justify-center">
            <i className="ri-questionnaire-line text-[#F8F6F3]/20 text-3xl" />
          </div>
          <p className="text-[#F8F6F3]/30 text-sm">Pulsa para añadir la primera pregunta</p>
        </div>
      )}

      {questions.map((q, qIndex) => {
        const hasAllExplanations = (q.option_explanations || []).filter(Boolean).length > 0;

        return (
          <div
            key={q.id}
            className="rounded-2xl overflow-hidden"
            style={{ background: 'rgba(248,246,243,0.04)', border: '1px solid rgba(248,246,243,0.08)' }}
          >
            {/* Question header */}
            <div
              className="flex items-center gap-3 px-5 py-4 cursor-pointer select-none"
              onClick={() => setExpandedId(expandedId === q.id ? null : q.id)}
            >
              <span
                className="text-[10px] font-mono w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0"
                style={{ background: 'rgba(139,157,131,0.15)', color: '#8B9D83' }}
              >
                {qIndex + 1}
              </span>
              <p className="flex-1 text-sm text-[#F8F6F3]/80 truncate">
                {q.question_text || <span className="text-[#F8F6F3]/30 italic">Sin pregunta...</span>}
              </p>
              <div className="flex items-center gap-2">
                {q.options[q.correct_index] && (
                  <span
                    className="text-[10px] px-2.5 py-0.5 rounded-full hidden sm:flex items-center gap-1"
                    style={{ background: 'rgba(139,157,131,0.12)', color: '#8B9D83' }}
                  >
                    <i className="ri-checkbox-circle-fill text-xs" />
                    Resp. marcada
                  </span>
                )}
                {hasAllExplanations && (
                  <span
                    className="text-[10px] px-2.5 py-0.5 rounded-full hidden sm:flex items-center gap-1"
                    style={{ background: 'rgba(201,123,99,0.10)', color: '#C97B63' }}
                  >
                    <i className="ri-chat-quote-line text-xs" />
                    Con explicaciones
                  </span>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); removeQuestion(q.id); }}
                  className="w-7 h-7 flex items-center justify-center rounded-lg transition-all duration-200 hover:opacity-70 cursor-pointer"
                  style={{ background: 'rgba(201,123,99,0.10)' }}
                >
                  <i className="ri-delete-bin-line text-[#C97B63] text-xs" />
                </button>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-arrow-${expandedId === q.id ? 'up' : 'down'}-s-line text-[#F8F6F3]/30 text-sm`} />
                </div>
              </div>
            </div>

            {/* Expanded content */}
            {expandedId === q.id && (
              <div className="px-5 pb-5 flex flex-col gap-5" style={{ borderTop: '1px solid rgba(248,246,243,0.06)' }}>
                {/* Question text */}
                <div className="flex flex-col gap-2 pt-4">
                  <label className="text-[#F8F6F3]/40 text-[10px] uppercase tracking-[2px]">Texto de la pregunta</label>
                  <textarea
                    value={q.question_text}
                    onChange={(e) => updateQuestion(q.id, { question_text: e.target.value })}
                    placeholder="Escribe aquí la pregunta..."
                    rows={2}
                    className="w-full px-4 py-3 rounded-xl text-sm text-[#F8F6F3] placeholder-[#F8F6F3]/20 outline-none resize-none"
                    style={{ background: 'rgba(248,246,243,0.06)', border: '1px solid rgba(248,246,243,0.10)' }}
                  />
                </div>

                {/* Options */}
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <label className="text-[#F8F6F3]/40 text-[10px] uppercase tracking-[2px]">
                      Opciones — <span className="text-[#8B9D83]">clic en el círculo para marcar la correcta</span>
                    </label>
                    {q.options.length < 6 && (
                      <button
                        onClick={() => addOption(q.id)}
                        className="text-[10px] text-[#8B9D83]/70 flex items-center gap-1 cursor-pointer hover:text-[#8B9D83] transition-colors"
                      >
                        <i className="ri-add-line" /> Añadir opción
                      </button>
                    )}
                  </div>

                  {q.options.map((opt, optIdx) => {
                    const isCorrect = q.correct_index === optIdx;
                    const isExplOpen = explanationOpenId === `${q.id}_${optIdx}`;
                    const explanation = (q.option_explanations || [])[optIdx] || '';

                    return (
                      <div key={optIdx} className="flex flex-col gap-0 rounded-xl overflow-hidden" style={{ border: `1px solid ${isCorrect ? 'rgba(139,157,131,0.30)' : 'rgba(248,246,243,0.08)'}` }}>
                        {/* Option row */}
                        <div
                          className="flex items-center gap-3 px-3 py-2.5"
                          style={{ background: isCorrect ? 'rgba(139,157,131,0.07)' : 'rgba(248,246,243,0.03)' }}
                        >
                          {/* Correct answer selector */}
                          <button
                            onClick={() => updateQuestion(q.id, { correct_index: optIdx })}
                            title="Marcar como respuesta correcta"
                            className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 cursor-pointer"
                            style={{
                              background: isCorrect ? 'rgba(139,157,131,0.25)' : 'rgba(248,246,243,0.05)',
                              border: `2px solid ${isCorrect ? '#8B9D83' : 'rgba(248,246,243,0.15)'}`,
                            }}
                          >
                            {isCorrect && <i className="ri-check-line text-[#8B9D83] text-sm" />}
                          </button>

                          {/* Option letter badge */}
                          <span
                            className="text-[10px] font-mono w-6 h-6 flex items-center justify-center rounded flex-shrink-0"
                            style={{
                              background: isCorrect ? 'rgba(139,157,131,0.15)' : 'rgba(248,246,243,0.06)',
                              color: isCorrect ? '#8B9D83' : 'rgba(248,246,243,0.30)',
                            }}
                          >
                            {String.fromCharCode(65 + optIdx)}
                          </span>

                          <input
                            type="text"
                            value={opt}
                            onChange={(e) => updateOption(q.id, optIdx, e.target.value)}
                            placeholder={`Opción ${String.fromCharCode(65 + optIdx)}`}
                            className="flex-1 px-3 py-1.5 rounded-lg text-sm text-[#F8F6F3] placeholder-[#F8F6F3]/20 outline-none"
                            style={{
                              background: 'transparent',
                              border: 'none',
                            }}
                          />

                          {/* Explanation toggle */}
                          <button
                            onClick={() => setExplanationOpenId(isExplOpen ? null : `${q.id}_${optIdx}`)}
                            title={isCorrect ? 'Por qué es correcta' : 'Por qué es incorrecta'}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0"
                            style={{
                              background: explanation
                                ? (isCorrect ? 'rgba(139,157,131,0.15)' : 'rgba(201,123,99,0.10)')
                                : 'rgba(248,246,243,0.06)',
                              color: explanation
                                ? (isCorrect ? '#8B9D83' : '#C97B63')
                                : 'rgba(248,246,243,0.30)',
                              border: explanation
                                ? `1px solid ${isCorrect ? 'rgba(139,157,131,0.25)' : 'rgba(201,123,99,0.20)'}`
                                : '1px solid transparent',
                            }}
                          >
                            <i className={`ri-chat-quote-line text-xs`} />
                            {isCorrect ? '¿Por qué es correcta?' : '¿Por qué es incorrecta?'}
                          </button>

                          {q.options.length > 2 && (
                            <button
                              onClick={() => removeOption(q.id, optIdx)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg transition-all duration-200 hover:opacity-70 cursor-pointer flex-shrink-0"
                              style={{ background: 'rgba(248,246,243,0.05)' }}
                            >
                              <i className="ri-close-line text-[#F8F6F3]/30 text-xs" />
                            </button>
                          )}
                        </div>

                        {/* Explanation panel */}
                        {isExplOpen && (
                          <div
                            className="px-4 py-3 flex flex-col gap-2"
                            style={{
                              borderTop: `1px solid ${isCorrect ? 'rgba(139,157,131,0.15)' : 'rgba(201,123,99,0.12)'}`,
                              background: isCorrect ? 'rgba(139,157,131,0.04)' : 'rgba(201,123,99,0.04)',
                            }}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <i className={`text-xs ${isCorrect ? 'ri-checkbox-circle-line text-[#8B9D83]' : 'ri-close-circle-line text-[#C97B63]'}`} />
                              <p className="text-[10px] uppercase tracking-[2px]" style={{ color: isCorrect ? '#8B9D83' : '#C97B63' }}>
                                {isCorrect ? 'Explica por qué esta respuesta ES la correcta' : 'Explica por qué esta respuesta NO es correcta'}
                              </p>
                            </div>
                            <textarea
                              value={explanation}
                              onChange={(e) => updateExplanation(q.id, optIdx, e.target.value)}
                              placeholder={
                                isCorrect
                                  ? 'Ej: Es correcta porque...'
                                  : 'Ej: No es correcta porque...'
                              }
                              rows={2}
                              className="w-full px-3 py-2.5 rounded-lg text-sm text-[#F8F6F3] placeholder-[#F8F6F3]/20 outline-none resize-none"
                              style={{
                                background: 'rgba(248,246,243,0.05)',
                                border: `1px solid ${isCorrect ? 'rgba(139,157,131,0.20)' : 'rgba(201,123,99,0.15)'}`,
                              }}
                            />
                            <p className="text-[#F8F6F3]/20 text-[10px]">
                              Esta explicación se mostrará al usuario cuando entregue sus respuestas.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Tip info */}
                <div
                  className="flex items-start gap-3 p-3 rounded-xl"
                  style={{ background: 'rgba(248,246,243,0.03)', border: '1px solid rgba(248,246,243,0.06)' }}
                >
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-lightbulb-line text-[#C97B63]/50 text-xs" />
                  </div>
                  <p className="text-[#F8F6F3]/25 text-[10px] leading-relaxed">
                    Añade explicaciones a cada opción pulsando el botón &quot;¿Por qué...?&quot;. Cuando el usuario entregue el cuestionario verá por qué su respuesta era correcta o incorrecta.
                  </p>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
